/**
 * OrderDetectedBadge - Badge shown in the email detail panel when the AI
 * detects an order in an email. Clicking opens the full OrderDetailModal
 * for order review, line-item editing, and special pricing lookup.
 *
 * When orderExists is false (agent classified as order but no order record
 * was created), shows a "Create Order" variant that lets the user manually
 * trigger order creation from the email.
 *
 * When siblingOrders contains more than one order, shows pill-style tabs
 * so the user can switch between sibling orders from the same email.
 */

import { useState } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import { trackEvent } from '../../services/portalTracking';
import { getOrderStatusDisplay } from '../../utils/orderStatusLabels';
import { attentionSummary } from '../../utils/reviewTier';
import type { OrderLineItem } from '../../types/orders';
import styles from './OrderDetectedBadge.module.css';

interface OrderPreview {
  poNumber?: string;
  products?: string;
  estValue?: string;
  confidence?: number | null;
  /**
   * Deterministic extraction-confidence bucket. When set, replaces the
   * numeric percentage on the badge with a colored high/medium/low pill.
   * NULL on rows from before the bucket rollout — the badge falls back
   * to the numeric percentage in that case.
   */
  confidenceBucket?: 'high' | 'medium' | 'low' | null;
}

export interface SiblingOrder {
  id: string;
  poNumber?: string | null;
  status: string;
  products?: string | null;
}

interface OrderDetectedBadgeProps {
  orderPreview: OrderPreview;
  emailId: string;
  /** Whether an order record already exists for this email. Defaults to true. */
  orderExists?: boolean;
  /** Raw order record status from the API (e.g. 'draft', 'submitted_to_sage'). */
  orderRecordStatus?: string | null;
  /** All orders from this email (for multi-order switching). */
  siblingOrders?: SiblingOrder[];
  onOrderAction?: (action: string, orderId: string) => void;
  onViewFullOrder?: (emailId: string) => void;
  /** Open a specific order by its ID (used for sibling switching). */
  onViewOrderById?: (orderId: string) => void;
  /**
   * Line items of the linked draft order when the caller has them from the
   * by-email response. Preferred source for the "N to answer" chip
   * (computed via attentionSummary).
   */
  lineItems?: OrderLineItem[] | null;
  /**
   * Fallback for the chip when line items aren't available: the order's
   * list-enriched attention_count. Null/undefined renders no chip.
   */
  attentionCount?: number | null;
}

/** Map order status to a CSS class for the colored dot. */
const statusDotClass = (status: string): string => {
  if (status === 'draft') return styles.siblingStatusDraft;
  if (status === 'submitted_to_sage' || status === 'submission_failed') return styles.siblingStatusSubmitted;
  if (status === 'confirmed_in_sage') return styles.siblingStatusConfirmed;
  if (status === 'shipped') return styles.siblingStatusShipped;
  if (status === 'order_completed' || status === 'accepted') return styles.siblingStatusCompleted;
  if (status === 'cancelled') return styles.siblingStatusCancelled;
  return styles.siblingStatusDraft;
};

const OrderDetectedBadge: React.FC<OrderDetectedBadgeProps> = ({
  orderPreview,
  emailId,
  orderExists = true,
  orderRecordStatus,
  siblingOrders = [],
  onViewFullOrder,
  onViewOrderById,
  lineItems,
  attentionCount,
}) => {
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [activeSiblingIdx, setActiveSiblingIdx] = useState(0);

  const hasSiblings = siblingOrders.length > 1;

  const confidencePct = orderPreview.confidence != null
    ? Math.round(orderPreview.confidence * 100)
    : null;

  // Prefer the deterministic bucket; fall back to numeric-derived class
  // for pre-rollout rows.
  const bucketClassMap = {
    high: styles.confidenceHigh,
    medium: styles.confidenceMedium,
    low: styles.confidenceLow,
  } as const;
  const confidenceClass = orderPreview.confidenceBucket
    ? bucketClassMap[orderPreview.confidenceBucket]
    : confidencePct != null
      ? confidencePct >= 80
        ? styles.confidenceHigh
        : confidencePct >= 60
          ? styles.confidenceMedium
          : styles.confidenceLow
      : '';
  const confidenceLabel = orderPreview.confidenceBucket
    ? `AI confidence: ${orderPreview.confidenceBucket}`
    : confidencePct != null
      ? `${confidencePct}%`
      : null;

  const handleCreateOrder = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const result = await portalOrderService.createOrderFromEmail(emailId);
      // Same row as CreateOrderFromEmailButton: the draft keyed by the
      // email it came from (kanban 1136a376).
      trackEvent('action', 'order_created', {
        order_id: result.order.id,
        email_id: emailId,
        source: 'email',
      });
      onViewFullOrder?.(emailId);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Failed to create order')
          : 'Failed to create order';
      setCreateError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleBadgeClick = () => {
    if (hasSiblings && onViewOrderById) {
      onViewOrderById(siblingOrders[activeSiblingIdx].id);
    } else {
      onViewFullOrder?.(emailId);
    }
  };

  const handleSiblingClick = (idx: number) => {
    setActiveSiblingIdx(idx);
    if (onViewOrderById) {
      onViewOrderById(siblingOrders[idx].id);
    }
  };

  // When no order exists AND no order record status is set — show "Create Order" variant.
  if (!orderExists && !orderRecordStatus) {
    return (
      <div className={styles.container}>
        <button
          className={`${styles.badge} ${styles.badgeNoOrder}`}
          onClick={handleCreateOrder}
          disabled={creating}
          type="button"
          data-action="create_order_from_email"
          data-target-email-id={emailId}
          data-outcome="request"
        >
          <div className={styles.badgeLeft}>
            <svg className={styles.badgeIcon} width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2z" />
            </svg>
            <div className={styles.badgeContent}>
              <div className={styles.badgeTitle}>
                {creating ? 'Creating Order...' : 'Order Classified'}
              </div>
              <div className={styles.badgePreview}>
                <span className={styles.previewItem}>
                  {creating ? 'Please wait' : 'No order record — click to create'}
                </span>
              </div>
            </div>
          </div>
          <div className={styles.badgeRight}>
            {confidenceLabel != null && (
              <span className={`${styles.confidenceBadge} ${confidenceClass}`}>
                {confidenceLabel}
              </span>
            )}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          </div>
        </button>
        {createError && (
          <div className={styles.createError}>{createError}</div>
        )}
      </div>
    );
  }

  // Determine which order to show in the badge
  const activeSibling = hasSiblings ? siblingOrders[activeSiblingIdx] : null;
  const displayStatus = activeSibling?.status ?? orderRecordStatus;
  const displayPo = activeSibling?.poNumber ?? orderPreview.poNumber;
  const displayProducts = activeSibling?.products ?? orderPreview.products;

  const statusDisplay = getOrderStatusDisplay(displayStatus);

  // Review-attention chip for draft orders: unresolved BLOCKER/VERIFY line
  // count from line items when the caller has them, else the list-enriched
  // attention_count, else no chip. Hidden for multi-order emails — the
  // props describe only the primary linked order.
  const attentionToAnswer: number | null =
    displayStatus === 'draft' && !hasSiblings
      ? lineItems
        ? attentionSummary(lineItems).toAnswer
        : attentionCount ?? null
      : null;

  const variantClass: Record<string, string | undefined> = {
    submitted: styles.badgeSubmitted,
    confirmed: styles.badgeConfirmed,
    shipped: styles.badgeShipped,
    completed: styles.badgeCompleted,
    cancelled: styles.badgeDanger,
    failed: styles.badgeDanger,
  };

  return (
    <div className={styles.container}>
      <button
        className={`${styles.badge} ${variantClass[statusDisplay.variant] ?? ''}`}
        onClick={handleBadgeClick}
        type="button"
        data-action="order_open"
        data-target-email-id={emailId}
        data-target-order-id={siblingOrders[activeSiblingIdx]?.id}
        data-outcome="open"
      >
        <div className={styles.badgeLeft}>
          <svg className={styles.badgeIcon} width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2z" />
          </svg>
          <div className={styles.badgeContent}>
            <div className={styles.badgeTitle}>
              {statusDisplay.badgeTitle}
              {hasSiblings && (
                <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 6, opacity: 0.7 }}>
                  ({activeSiblingIdx + 1} of {siblingOrders.length})
                </span>
              )}
            </div>
            <div className={styles.badgePreview}>
              {displayPo && (
                <span className={styles.previewItem}>PO: {displayPo}</span>
              )}
              {displayProducts && (
                <span className={styles.previewItem}>{displayProducts}</span>
              )}
              {!displayPo && !displayProducts && (
                <span className={styles.previewItem}>Click to review order</span>
              )}
            </div>
          </div>
        </div>
        <div className={styles.badgeRight}>
          {attentionToAnswer != null && (
            attentionToAnswer > 0 ? (
              <span
                data-testid="badge-attention-chip"
                title={`${attentionToAnswer} review item${attentionToAnswer === 1 ? '' : 's'} awaiting an answer`}
                style={{
                  background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
                  borderRadius: '4px', padding: '1px 6px', fontSize: '10px',
                  fontWeight: 700, whiteSpace: 'nowrap',
                }}
              >
                {attentionToAnswer} to answer
              </span>
            ) : (
              <span
                data-testid="badge-attention-chip"
                title="No review items left to answer"
                style={{
                  background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7',
                  borderRadius: '4px', padding: '1px 6px', fontSize: '10px',
                  fontWeight: 700, whiteSpace: 'nowrap',
                }}
              >
                Ready for Sage
              </span>
            )
          )}
          {confidenceLabel != null && (
            <span className={`${styles.confidenceBadge} ${confidenceClass}`}>
              {confidenceLabel}
            </span>
          )}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        </div>
      </button>

      {/* Sibling order pills — only shown for multi-order emails */}
      {hasSiblings && (
        <div className={styles.siblingNav}>
          {siblingOrders.map((sib, idx) => (
            <button
              key={sib.id}
              type="button"
              className={`${styles.siblingPill} ${idx === activeSiblingIdx ? styles.siblingPillActive : ''}`}
              onClick={(e) => { e.stopPropagation(); handleSiblingClick(idx); }}
              data-action="order_open"
              data-target-order-id={sib.id}
              data-target-email-id={emailId}
              data-outcome="open"
            >
              <span className={`${styles.siblingPillStatus} ${statusDotClass(sib.status)}`} />
              {sib.poNumber ? `PO: ${sib.poNumber}` : `Order ${idx + 1}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderDetectedBadge;
